<template>
  <!-- Balances -->
  <section v-if="filteredBalances && filteredBalances.length > 0" class="fiat-balances-wrapper">
    <div class="balances-wrapper ">
      <dl>
        <div
          :key="bal.id"
          v-for="bal in filteredBalances"
          :class="bal.currency">
          <dt>
            {{ bal.currency }}
            {{ bal.crypto_blockchain ? `(${bal.crypto_blockchain})` : '' }}
          </dt>
          <dd>
            <span>{{
              bal.type === 'fiat_money'
                ? formatFiatPrice(bal.available_balance)
                : formatCryptoAmount(bal.available_balance, bal.currency)
            }}</span>
          </dd>
        </div>
      </dl>
    </div>

    <!-- Only display if there is value in the deposit_address field in the balances -->
    <label class="collapse" v-if="shouldDisplayStaticAddresses">
      Top up balance with crypto
      <input type="checkbox" v-model.trim="displayStaticAddress" />
    </label>
    <Transition name="fade" v-if="displayStaticAddress">
      <div class="static-address-wrapper">
        <p>
          Transfer funds to the relevant wallet address(es) below, ensuring the
          token and network match your intended transfer.
        </p>
        <table>
          <tr>
            <th>Token</th>
            <th>Wallet Address</th>
          </tr>
          <tr v-for="bal in staticAddresses" :key="bal.id">
            <td>
              <div>
                <img
                  :src="require(`../../assets/images/logo/${bal.currency.toLowerCase()}.svg`)
                    "
                  width="25px" />
                <span>{{ bal.currency }}</span>
                {{
                  bal.crypto_blockchain
                    ? `(${networkNameMapping(bal.crypto_blockchain)} network)`
                    : ''
                }}
              </div>
            </td>
            <td>
              {{ bal.crypto_address }}
              <div>
                <vs-icon
                  icon="content_copy"
                  size="x-small"
                  @click="copyStaticAddress(bal.crypto_address, bal.id)"
                  style="cursor: pointer; margin: 0 5px"></vs-icon>
                <div class="copy-tooltip" v-if="tooltipVisible[bal.id]">
                  Copied
                </div>
              </div>
            </td>
          </tr>
        </table>
      </div>
    </Transition>
  </section>
</template>
<script>
export default {
  props: {
    balanceList: {
      type: Array,
      required: false,
    },
    title: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      filteredBalances: this.getFilteredBalances(),
      staticAddresses: this.getBalancesWithStaticAddress(),
      displayStaticAddress: false,
      tooltipVisible: {},
    };
  },
  methods: {
    getFilteredBalances() {
      // filter out all balances that are cryptocurrencies and equal to zero
      if (this.balanceList && this.balanceList.length > 0) {
        return this.balanceList.filter(
          (bal) => !this.isZeroCryptoCurrency(bal.currency, bal.available_balance)
        );
      } else {
        return this.$store.getters.fiatPayoutBalanceListGetter.filter(
          (bal) => !this.isZeroCryptoCurrency(bal.currency, bal.available_balance)
        );
      }
    },

    async getRequestToken() {
      const merchantKey = this.$store.getters.activeMerchantKeyGetter;
      const response = await this.$store.dispatch(
        'getMerchantPaymentRequestToken',
        merchantKey
      );
      this.requestToken = response.data.access_token;
      return response.data.access_token;
    },

    async fetchBalances() {
      const token = await this.getRequestToken();
      await this.$store.dispatch('fetchFiatPayoutBalancesAction', {
        accessToken: token,
      });
    },
    getBalancesWithStaticAddress() {
      return this.$store.getters.fiatPayoutBalanceListGetter
        .filter(
          (balance) =>
            balance.type === 'cryptocurrency' && balance.crypto_address
        )
        .map((balance) => {
          return {
            id: balance.id,
            currency: balance.currency,
            crypto_blockchain: balance.crypto_blockchain,
            crypto_address: balance.crypto_address,
          };
        });
    },
    copyStaticAddress(item, id) {
      if (item) {
        this.copyToClipboard(item);
        // Show the tooltip for the clicked item
        this.$set(this.tooltipVisible, id, true);
      }
      setTimeout(() => {
        this.$set(this.tooltipVisible, id, false);
      }, 1000);
    },

    imagePath(currency) {
      return `../../assets/images/logo/${currency.toLowerCase()}.png`;
    },
  },

  computed: {
    activeMerchant() {
      return this.$store.getters.activeMerchantGetter;
    },
    shouldDisplayStaticAddresses() {
      return this.$store.getters.fiatPayoutBalanceListGetter.some(
        (bal) => bal.crypto_address
      );
    },
  },
  watch: {
    activeMerchant: {
      handler() {
        // get token and get fiat balances
        this.fetchBalances().then(() => {
          this.filteredBalances = this.getFilteredBalances();
        });
      },
    },
  },
};
</script>
<style lang="scss">
.fiat-balances-wrapper {
  &>.balances-wrapper {
    dl {
      div {
        align-items: center;

        dt {
          font-weight: normal;
        }
      }
    }
  }

  label.collapse {
    font-size: 14px;

    &:hover {
      text-decoration: underline;
    }

    input {
      appearance: none;

      &::before {
        display: none;
      }

      &::after {
        content: url('../../assets/icons/expand.svg');
        transform: rotate(-90deg);
        display: inline-block;
        position: relative;
        top: 6px;
        transition: 0.3s;
      }

      &:checked::after {
        content: url('../../assets/icons/expand.svg');
        transform: rotate(360deg);
      }
    }
  }

  &>.static-address-wrapper {
    border-radius: 10px;
    margin: 10px 0;
    padding: 10px;
    width: max-content;

    p {
      font-weight: 400;
      margin-bottom: 10px;
    }

    table {
      border: 3px solid $color-table;
      border-spacing: 0px !important;
      border-radius: 6px;

      tr {
        border-bottom: 3px solid $color-table;

        th {
          border-bottom: 3px solid $color-table;
        }

        th:nth-child(1) {
          border-right: 3px solid $color-table;
        }

        td {
          min-height: 50px;
          padding: 10px;
          border-bottom: 3px solid $color-table;
          border-right: 3px solid $color-table;
        }

        td:nth-child(1) {
          div {
            display: flex;
            justify-content: flex-start;
            align-items: center;

            img {
              margin-right: 5px;
            }

            span {
              font-weight: 600;
              margin: 0 3px;
            }
          }
        }

        td:nth-child(2) {
          display: flex;
          align-items: center;

          div {
            position: relative;
          }

          .copy-tooltip {
            position: absolute;
            top: -10px;
            left: 25px;
            z-index: 1;
            visibility: visible;
            background: #151745;
            padding: 10px;
            border-radius: 10px;
            font: normal 12px;
            color: #ffffff;
            white-space: normal;
          }
        }
      }

      tr:last-child td {
        border-bottom: none;
      }

      td:last-child {
        border-right: none;
      }
    }
  }
}
</style>
